import { QrApi, QrCategoryApi, QrTagApi } from '../api';
import { openDialog } from '@/utils/utils';

/**
 * URL 인코딩 함수 (한글 등 유니코드 문자 자동 인코딩)
 * @param {string} urlString - 원본 URL
 * @returns {string} 인코딩된 URL
 */
const encodeUrlPath = (urlString) => {
  if (!urlString) return '';
  try {
    const url = urlString.trim();
    const urlWithProtocol = url.match(/^https?:\/\//) ? url : `https://${url}`;
    const urlObj = new URL(urlWithProtocol);
    return urlObj.href;
  } catch {
    return encodeURI(urlString);
  }
};

/**
 * QR코드 데이터의 targetUrl 필드를 인코딩
 * @param {Object} data - QR코드 데이터
 * @returns {Object} targetUrl이 인코딩된 데이터
 */
const normalizeQrData = (data) => {
  if (!data) return data;
  if (data.targetUrl) {
    return { ...data, targetUrl: encodeUrlPath(data.targetUrl) };
  }
  return data;
};

/**
 * 대량 데이터의 각 항목 targetUrl 인코딩
 * @param {Object} bulkData - 대량 생성/검증 데이터
 * @returns {Object} targetUrl이 인코딩된 데이터
 */
const normalizeBulkData = (bulkData) => {
  if (!bulkData?.items) return bulkData;
  return {
    ...bulkData,
    items: bulkData.items.map(item => normalizeQrData(item))
  };
};

/**
 * QR코드 서비스 클래스
 * API 호출과 데이터 처리 로직을 분리하여 관리합니다.
 */
export default class QrService {
  /**
   * QR코드 목록 조회
   * @param {Object} params - 검색 파라미터
   * @returns {Promise<{items: Array, total: number, totalPages: number}>}
   */
  static async getQrList(params) {
    try {
      const response = await QrApi.getQrList(params);

      if (response.data) {
        return {
          items: response.data.items || [],
          total: response.data.total || 0,
          totalPages: Math.ceil((response.data.total || 0) / (params.limit || 10))
        };
      }

      return { items: [], total: 0, totalPages: 0 };
    } catch (error) {
      // 조회 실패 시 콘솔에만 로그하고 빈 데이터 반환 (팝업 표시 안함)
      console.error('QR코드 목록 조회 오류:', error);
      return { items: [], total: 0, totalPages: 0 };
    }
  }

  /**
   * QR코드 상세 조회
   * @param {string} qrId - QR코드 ID
   * @returns {Promise<Object|null>}
   */
  static async getQr(qrId) {
    try {
      const response = await QrApi.getQr(qrId);
      return response.data;
    } catch (error) {
      console.error('QR코드 상세 조회 오류:', error);
      return null;
    }
  }

  /**
   * QR코드 생성
   * @param {Object} qrData - QR코드 데이터
   * @returns {Promise<Object|null>} 생성된 QR코드 정보 또는 null
   */
  static async createQr(qrData) {
    try {
      const response = await QrApi.createQr(normalizeQrData(qrData));
      await openDialog('QR코드가 생성되었습니다.', 'success', '확인');
      return response.data;
    } catch (error) {
      this.handleApiError(error, 'QR코드 생성 중 오류가 발생했습니다.');
      return null;
    }
  }

  /**
   * QR코드 수정
   * @param {string} qrId - QR코드 ID
   * @param {Object} qrData - QR코드 데이터
   * @returns {Promise<boolean>}
   */
  static async updateQr(qrId, qrData) {
    try {
      await QrApi.updateQr(qrId, normalizeQrData(qrData));
      await openDialog('QR코드가 수정되었습니다.', 'success', '확인');
      return true;
    } catch (error) {
      this.handleApiError(error, 'QR코드 수정 중 오류가 발생했습니다.');
      return false;
    }
  }

  /**
   * QR코드 삭제
   * @param {string} qrId - QR코드 ID
   * @returns {Promise<boolean>}
   */
  static async deleteQr(qrId) {
    try {
      await QrApi.deleteQr(qrId);
      await openDialog('QR코드가 삭제되었습니다.', 'success', '확인');
      return true;
    } catch (error) {
      this.handleApiError(error, 'QR코드 삭제 중 오류가 발생했습니다.');
      return false;
    }
  }

  /**
   * QR코드 목적지 URL 변경
   * @param {string} qrId - QR코드 ID
   * @param {string} targetUrl - 새 목적지 URL
   * @returns {Promise<boolean>}
   */
  static async updateTargetUrl(qrId, targetUrl) {
    try {
      await QrApi.updateTargetUrl(qrId, encodeUrlPath(targetUrl));
      await openDialog('목적지 URL이 변경되었습니다.', 'success', '확인');
      return true;
    } catch (error) {
      this.handleApiError(error, 'URL 변경 중 오류가 발생했습니다.');
      return false;
    }
  }

  /**
   * QR코드 URL 변경 이력 조회
   * @param {string} qrId - QR코드 ID
   * @param {Object} [params] - 페이지네이션 파라미터
   * @returns {Promise<{items: Array, total: number}>}
   */
  static async getUrlHistory(qrId, params = {}) {
    try {
      const response = await QrApi.getUrlHistory(qrId, params);

      if (response.data) {
        return {
          items: response.data.items || [],
          total: response.data.total || 0
        };
      }

      return { items: [], total: 0 };
    } catch (error) {
      console.error('URL 변경 이력 조회 오류:', error);
      return { items: [], total: 0 };
    }
  }

  /**
   * QR코드 URL 복원
   * @param {string} qrId - QR코드 ID
   * @param {string} historyId - 이력 ID
   * @returns {Promise<boolean>}
   */
  static async restoreUrl(qrId, historyId) {
    try {
      await QrApi.restoreUrl(qrId, historyId);
      await openDialog('URL이 복원되었습니다.', 'success', '확인');
      return true;
    } catch (error) {
      this.handleApiError(error, 'URL 복원 중 오류가 발생했습니다.');
      return false;
    }
  }

  /**
   * QR코드 상태 변경
   * @param {string} qrId - QR코드 ID
   * @param {string} status - 상태
   * @returns {Promise<boolean>}
   */
  static async updateStatus(qrId, status) {
    try {
      await QrApi.updateStatus(qrId, status);
      const statusText = status === 'active' ? '활성화' : '비활성화';
      await openDialog(`QR코드가 ${statusText}되었습니다.`, 'success', '확인');
      return true;
    } catch (error) {
      this.handleApiError(error, '상태 변경 중 오류가 발생했습니다.');
      return false;
    }
  }

  /**
   * 대시보드 통계 조회
   * @param {Object} [params] - 조회 파라미터
   * @returns {Promise<Object>}
   */
  static async getDashboardStats(params = {}) {
    try {
      const response = await QrApi.getDashboardStats(params);
      return response.data || {};
    } catch (error) {
      console.error('대시보드 통계 조회 오류:', error);
      return {};
    }
  }

  /**
   * 카테고리 목록 조회
   * @returns {Promise<Array>}
   */
  static async getCategories() {
    try {
      const response = await QrCategoryApi.getCategories();
      return response.data?.items || response.data || [];
    } catch (error) {
      console.error('카테고리 목록 조회 오류:', error);
      return [];
    }
  }

  /**
   * 카테고리 생성
   * @param {Object} categoryData - 카테고리 데이터
   * @returns {Promise<boolean>}
   */
  static async createCategory(categoryData) {
    try {
      await QrCategoryApi.createCategory(categoryData);
      await openDialog('카테고리가 생성되었습니다.', 'success', '확인');
      return true;
    } catch (error) {
      this.handleApiError(error, '카테고리 생성 중 오류가 발생했습니다.');
      return false;
    }
  }

  /**
   * 카테고리 수정
   * @param {string} categoryId - 카테고리 ID
   * @param {Object} categoryData - 카테고리 데이터
   * @returns {Promise<boolean>}
   */
  static async updateCategory(categoryId, categoryData) {
    try {
      await QrCategoryApi.updateCategory(categoryId, categoryData);
      await openDialog('카테고리가 수정되었습니다.', 'success', '확인');
      return true;
    } catch (error) {
      this.handleApiError(error, '카테고리 수정 중 오류가 발생했습니다.');
      return false;
    }
  }

  /**
   * 카테고리 삭제
   * @param {string} categoryId - 카테고리 ID
   * @returns {Promise<boolean>}
   */
  static async deleteCategory(categoryId) {
    try {
      await QrCategoryApi.deleteCategory(categoryId);
      await openDialog('카테고리가 삭제되었습니다.', 'success', '확인');
      return true;
    } catch (error) {
      this.handleApiError(error, '카테고리 삭제 중 오류가 발생했습니다.');
      return false;
    }
  }

  /**
   * 태그 목록 조회
   * @returns {Promise<Array>}
   */
  static async getTags() {
    try {
      const response = await QrTagApi.getTags();
      return response.data?.items || response.data || [];
    } catch (error) {
      console.error('태그 목록 조회 오류:', error);
      return [];
    }
  }

  /**
   * 태그 생성
   * @param {Object} tagData - 태그 데이터
   * @returns {Promise<boolean>}
   */
  static async createTag(tagData) {
    try {
      await QrTagApi.createTag(tagData);
      await openDialog('태그가 생성되었습니다.', 'success', '확인');
      return true;
    } catch (error) {
      this.handleApiError(error, '태그 생성 중 오류가 발생했습니다.');
      return false;
    }
  }

  /**
   * 태그 수정
   * @param {string} tagId - 태그 ID
   * @param {Object} tagData - 태그 데이터
   * @returns {Promise<boolean>}
   */
  static async updateTag(tagId, tagData) {
    try {
      await QrTagApi.updateTag(tagId, tagData);
      await openDialog('태그가 수정되었습니다.', 'success', '확인');
      return true;
    } catch (error) {
      this.handleApiError(error, '태그 수정 중 오류가 발생했습니다.');
      return false;
    }
  }

  /**
   * 태그 삭제
   * @param {string} tagId - 태그 ID
   * @returns {Promise<boolean>}
   */
  static async deleteTag(tagId) {
    try {
      await QrTagApi.deleteTag(tagId);
      await openDialog('태그가 삭제되었습니다.', 'success', '확인');
      return true;
    } catch (error) {
      this.handleApiError(error, '태그 삭제 중 오류가 발생했습니다.');
      return false;
    }
  }

  /**
   * QR코드 대량 생성
   * @param {Object} bulkData - 대량 생성 데이터
   * @param {Array} bulkData.items - QR코드 항목 배열
   * @param {Object} [bulkData.options] - 생성 옵션
   * @returns {Promise<Object>} 생성 결과 (success, failed, created, errors)
   */
  static async bulkCreate(bulkData) {
    try {
      const response = await QrApi.bulkCreate(normalizeBulkData(bulkData));
      const result = response.data;

      if (result.success > 0 && result.failed === 0) {
        await openDialog(`${result.success}개의 QR코드가 생성되었습니다.`, 'success', '확인');
      } else if (result.success > 0 && result.failed > 0) {
        await openDialog(
          `총 ${result.success + result.failed}개 중 ${result.success}개 생성 성공, ${result.failed}개 실패`,
          'warning',
          '확인'
        );
      } else {
        await openDialog('QR코드 생성에 실패했습니다.', 'error', '확인');
      }

      return result;
    } catch (error) {
      this.handleApiError(error, 'QR코드 대량 생성 중 오류가 발생했습니다.');
      return { success: 0, failed: 0, created: [], errors: [] };
    }
  }

  /**
   * QR코드 대량 생성 검증 (미리보기)
   * @param {Object} bulkData - 대량 생성 데이터
   * @returns {Promise<Object|null>} 검증 결과, 요청 실패 시 null (오류 팝업 표시)
   */
  static async bulkValidate(bulkData) {
    try {
      const response = await QrApi.bulkValidate(normalizeBulkData(bulkData));
      return response.data;
    } catch (error) {
      // 빈 결과를 돌려주면 화면이 모든 행을 '정상'으로 표시하고 생성 버튼까지 켜진다.
      await this.handleApiError(error, 'QR코드 검증 중 오류가 발생했습니다.');
      return null;
    }
  }

  /**
   * API 오류 처리
   * @param {Error} error - 에러 객체
   * @param {string} message - 오류 메시지
   * @private
   */
  static async handleApiError(error, message) {
    console.error(message, error);
    const errorMessage = error.response?.data?.message || message;
    await openDialog(errorMessage, 'error', '확인');
  }
}
